import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './DefinitionOfDone.css';

export interface DefinitionOfDoneCriteria {
  id: string;
  title?: string;
  label?: string; // Alternative to title for backward compatibility
  description: string;
  required?: boolean;
  completed: boolean;
  evidence?: string;
  validationMethod?: 'manual' | 'automatic' | 'external' | 'survey';
  helpText?: string;
  surveyQuestions?: SurveyQuestion[];
  surveyResponses?: Record<string, any>;
  disabled?: boolean; // Criterion cannot be interacted with when disabled
  disabledReason?: string; // Explanation for why it's disabled
}

export interface SurveyQuestion {
  id: string;
  question: string;
  type: 'yes-no' | 'scale' | 'text';
  required?: boolean;
  scaleMin?: number;
  scaleMax?: number;
  scaleLabel?: { min: string; max: string };
}

export interface DefinitionOfDoneProps {
  stepId?: string;
  stepName?: string; // Alternative to stepId for backward compatibility
  criteria: DefinitionOfDoneCriteria[];
  onCriteriaUpdate?: (criteria: DefinitionOfDoneCriteria[]) => void;
  onCriteriaChange?: (criteria: DefinitionOfDoneCriteria[]) => void; // Alternative callback name
  onComplete?: () => void;
  readOnly?: boolean;
  showProgress?: boolean;
}

export const DefinitionOfDone: React.FC<DefinitionOfDoneProps> = ({
  stepId,
  stepName,
  criteria,
  onCriteriaUpdate,
  onCriteriaChange,
  onComplete,
  readOnly = false,
  showProgress = true
}) => {
  const { t } = useLanguage();
  const [localCriteria, setLocalCriteria] = useState<DefinitionOfDoneCriteria[]>(criteria);
  const [expandedSurvey, setExpandedSurvey] = useState<string | null>(null);
  const effectiveStepId = stepId || stepName || 'default';
  const effectiveOnUpdate = onCriteriaUpdate || onCriteriaChange;

  useEffect(() => {
    setLocalCriteria(criteria);
  }, [criteria]);

  const handleCriteriaToggle = (criteriaId: string) => {
    if (readOnly) return;

    // Check if criterion is disabled
    const criterion = localCriteria.find(c => c.id === criteriaId);
    if (criterion?.disabled) {
      // Don't allow toggling disabled criteria
      return;
    }

    const updatedCriteria = localCriteria.map(criterion =>
      criterion.id === criteriaId
        ? { ...criterion, completed: !criterion.completed }
        : criterion
    );

    setLocalCriteria(updatedCriteria);
    if (effectiveOnUpdate) {
      effectiveOnUpdate(updatedCriteria);
    }

    // Check if all required criteria are completed
    const allRequiredCompleted = updatedCriteria
      .filter(c => c.required)
      .every(c => c.completed);

    if (allRequiredCompleted && onComplete) {
      onComplete();
    }
  };

  const handleEvidenceUpdate = (criteriaId: string, evidence: string) => {
    if (readOnly) return;

    const updatedCriteria = localCriteria.map(criterion =>
      criterion.id === criteriaId
        ? { ...criterion, evidence }
        : criterion
    );

    setLocalCriteria(updatedCriteria);
    if (effectiveOnUpdate) {
      effectiveOnUpdate(updatedCriteria);
    }
  };

  const handleSurveyResponse = (criteriaId: string, questionId: string, value: any) => {
    if (readOnly) return;

    const updatedCriteria = localCriteria.map(criterion => {
      if (criterion.id === criteriaId) {
        const responses = { ...(criterion.surveyResponses || {}), [questionId]: value };
        const allRequired = criterion.surveyQuestions?.filter(q => q.required) || [];
        const allAnswered = allRequired.every(q => responses[q.id] !== undefined && responses[q.id] !== '');

        return {
          ...criterion,
          surveyResponses: responses,
          completed: allAnswered
        };
      }
      return criterion;
    });

    setLocalCriteria(updatedCriteria);
    if (effectiveOnUpdate) {
      effectiveOnUpdate(updatedCriteria);
    }
  };

  const completedCount = localCriteria.filter(c => c.completed).length;
  const requiredCount = localCriteria.filter(c => c.required).length;
  const totalCount = localCriteria.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="definition-of-done">
      <div className="dod-header">
        <h3>{t('definitionOfDone.title')}</h3>
        {showProgress && (
          <div className="dod-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="progress-text">
              {t('definitionOfDone.completedCount', { completed: completedCount, total: totalCount })}
            </span>
          </div>
        )}
      </div>

      <div className="dod-criteria">
        {localCriteria.map((criterion) => (
          <div
            key={criterion.id}
            className={`criterion ${criterion.completed ? 'completed' : ''} ${criterion.required ? 'required' : ''} ${criterion.validationMethod === 'survey' ? 'verification-survey' : ''} ${criterion.disabled ? 'disabled' : ''}`}
            onClick={() => {
              if (!readOnly && !criterion.disabled) {
                handleCriteriaToggle(criterion.id);
              }
            }}
            style={{ cursor: (readOnly || criterion.disabled) ? 'not-allowed' : 'pointer' }}
          >
            <div className="criterion-header">
              <label
                className="criterion-checkbox"
                onClick={(e) => {
                  e.stopPropagation();
                  if (criterion.disabled) {
                    e.preventDefault();
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={criterion.completed}
                  onChange={(e) => {
                    if (!criterion.disabled) {
                      handleCriteriaToggle(criterion.id);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  disabled={readOnly || criterion.disabled}
                />
                <span className="checkmark"></span>
              </label>
              <div className="criterion-info">
                <h4 className="criterion-title">
                  {criterion.title}
                  {criterion.required && <span className="required-badge">Required</span>}
                  {criterion.disabled && <span className="disabled-badge">🔒 Locked</span>}
                </h4>
                <p className="criterion-description">{criterion.description}</p>
                {criterion.disabled && criterion.disabledReason && (
                  <p className="criterion-disabled-reason">
                    <i className="bi bi-info-circle"></i> {criterion.disabledReason}
                  </p>
                )}
              </div>
            </div>



            {criterion.validationMethod === 'survey' && criterion.surveyQuestions && (
              <div className="criterion-survey">
                <button
                  className="survey-toggle"
                  onClick={() => setExpandedSurvey(expandedSurvey === criterion.id ? null : criterion.id)}
                  type="button"
                >
                  {expandedSurvey === criterion.id ? '▼' : '▶'}
                  {expandedSurvey === criterion.id ? 'Hide' : 'Show'} Questions
                  ({Object.keys(criterion.surveyResponses || {}).length}/{criterion.surveyQuestions.length} answered)
                </button>

                {expandedSurvey === criterion.id && (
                  <div className="survey-questions">
                    {criterion.surveyQuestions.map((question, idx) => (
                      <div key={question.id} className="survey-question">
                        <label className="survey-question-label">
                          {idx + 1}. {question.question}
                          {question.required && <span className="required-indicator">*</span>}
                        </label>

                        {question.type === 'yes-no' && (
                          <div className="survey-options">
                            <label className="survey-option">
                              <input
                                type="radio"
                                name={`${criterion.id}-${question.id}`}
                                value="yes"
                                checked={criterion.surveyResponses?.[question.id] === 'yes'}
                                onChange={(e) => handleSurveyResponse(criterion.id, question.id, e.target.value)}
                                disabled={readOnly}
                              />
                              Yes
                            </label>
                            <label className="survey-option">
                              <input
                                type="radio"
                                name={`${criterion.id}-${question.id}`}
                                value="no"
                                checked={criterion.surveyResponses?.[question.id] === 'no'}
                                onChange={(e) => handleSurveyResponse(criterion.id, question.id, e.target.value)}
                                disabled={readOnly}
                              />
                              No
                            </label>
                          </div>
                        )}

                        {question.type === 'scale' && (
                          <div className="survey-scale">
                            <div className="scale-labels">
                              <span>{question.scaleLabel?.min || question.scaleMin || 1}</span>
                              <span>{question.scaleLabel?.max || question.scaleMax || 5}</span>
                            </div>
                            <input
                              type="range"
                              min={question.scaleMin || 1}
                              max={question.scaleMax || 5}
                              value={criterion.surveyResponses?.[question.id] || question.scaleMin || 1}
                              onChange={(e) => handleSurveyResponse(criterion.id, question.id, parseInt(e.target.value))}
                              disabled={readOnly}
                              className="scale-input"
                            />
                            <div className="scale-value">
                              {criterion.surveyResponses?.[question.id] || question.scaleMin || 1}
                            </div>
                          </div>
                        )}

                        {question.type === 'text' && (
                          <textarea
                            value={criterion.surveyResponses?.[question.id] || ''}
                            onChange={(e) => handleSurveyResponse(criterion.id, question.id, e.target.value)}
                            placeholder="Your answer..."
                            disabled={readOnly}
                            rows={3}
                            className="survey-text-input"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {localCriteria.filter(c => c.required).every(c => c.completed) && (
        <div className="dod-completion-notice">
          {t('definitionOfDone.completionNotice')}
        </div>
      )}
    </div>
  );
};

export default DefinitionOfDone;